import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Pencil, Loader2, ArrowLeft, ShoppingCart, ChefHat, Copy, Trash2 } from "lucide-react";
import { MobileContainer } from "@/components/MobileContainer";
import { OnboardingCarousel } from "@/components/OnboardingCarousel";
import { TrustPledgeModal } from "@/components/TrustPledgeModal";
import { AuthScreen } from "@/components/AuthScreen";
import { DietaryPreferences, DietaryPreferencesData } from "@/components/DietaryPreferences";
import { DietaryRestrictionsEditor } from "@/components/DietaryRestrictionsEditor";
import { GoalSelection } from "@/components/GoalSelection";
import { GroceryItem, GroceryItemData } from "@/components/GroceryItem";
import { SwapModal } from "@/components/SwapModal";
import { BottomNav } from "@/components/BottomNav";
import { SuccessOverlay } from "@/components/SuccessOverlay";
import { SettingsView } from "@/components/SettingsView";
import { HistoryView } from "@/components/HistoryView";
import { SkeletonLoader } from "@/components/SkeletonLoader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useGroceryList } from "@/hooks/useGroceryList";
import { useUserProfile } from "@/hooks/useUserProfile";
import { HistoryItem } from "@/hooks/useGroceryHistory";

type AppStep = "onboarding" | "trust" | "auth" | "dietary" | "goals" | "home";
type Tab = "history" | "home" | "settings";

const goalLabels: Record<string, string> = {
  gut: "Gut & Digestion", brain: "Brain & Focus", heart: "Heart & Cardiovascular",
  energy: "Energy & Vitality", immunity: "Immunity & Defense", sleep: "Sleep & Recovery",
  bones: "Bones & Joints", skin: "Skin & Radiance",
};

const Index = () => {
  const { user, loading, isGuest, signOut } = useAuth();
  const { isGenerating, isLoadingSwap, generateList, getAlternatives } = useGroceryList();
  const { 
    isLoading: isProfileLoading, 
    dietaryPreferences, 
    healthGoals, 
    customGoal: savedCustomGoal,
    saveDietaryPreferences,
    saveHealthGoals 
  } = useUserProfile(user?.id, isGuest);
  
  const [step, setStep] = useState<AppStep>("trust");
  const [activeTab, setActiveTab] = useState<Tab>("home");
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [customGoal, setCustomGoal] = useState<string>("");
  const [localDietaryPreferences, setLocalDietaryPreferences] = useState<DietaryPreferencesData>({ lifestyles: [], allergens: [] });
  const [groceryList, setGroceryList] = useState<GroceryItemData[]>([]);
  const [swapAlternatives, setSwapAlternatives] = useState<GroceryItemData[]>([]);
  const [swapItemId, setSwapItemId] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isGuestView, setIsGuestView] = useState(false);
  const [viewingHistoryItem, setViewingHistoryItem] = useState<HistoryItem | null>(null);
  const [isRestrictionsSheetOpen, setIsRestrictionsSheetOpen] = useState(false);

  // Sync profile data to local state when loaded
  useEffect(() => {
    if (!isProfileLoading) {
      if (healthGoals.length > 0) setSelectedGoals(healthGoals);
      if (savedCustomGoal) setCustomGoal(savedCustomGoal);
      setLocalDietaryPreferences(dietaryPreferences);
    }
  }, [isProfileLoading, healthGoals, savedCustomGoal, dietaryPreferences]);

  // Handle auth state and routing
  useEffect(() => {
    if (loading || isProfileLoading) return;

    const hasOnboarded = localStorage.getItem("metabolic_onboarded");
    const savedList = localStorage.getItem("metabolic_list");

    if (user) {
      // User is authenticated
      // Check if they have goals (from DB or localStorage) to determine onboarding status
      const hasGoals = healthGoals.length > 0 || savedCustomGoal;
      if (hasOnboarded || hasGoals) {
        setStep("home");
        if (savedList) setGroceryList(JSON.parse(savedList));
        // Mark as onboarded if they have data
        if (hasGoals && !hasOnboarded) {
          localStorage.setItem("metabolic_onboarded", "true");
        }
      } else {
        // Authenticated but hasn't completed onboarding - go to dietary first
        setStep("dietary");
      }
    } else {
      // Not authenticated - show welcome/onboarding flow
      setStep("trust");
    }
  }, [user, loading, isProfileLoading, healthGoals, savedCustomGoal]);

  useEffect(() => {
    if (selectedGoals.length > 0) localStorage.setItem("metabolic_goals", JSON.stringify(selectedGoals));
  }, [selectedGoals]);

  useEffect(() => {
    if (groceryList.length > 0) localStorage.setItem("metabolic_list", JSON.stringify(groceryList));
  }, [groceryList]);

  const handleTrustContinue = () => setStep("onboarding");
  const handleOnboardingComplete = () => setStep("auth");
  const handleOnboardingBack = () => setStep("trust");
  const handleAuthBack = () => setStep("onboarding");
  const handleAuthSuccess = () => setStep("dietary");
  const handleGuestContinue = () => setStep("dietary");
  
  const handleDietaryComplete = async (preferences: DietaryPreferencesData) => {
    setLocalDietaryPreferences(preferences);
    await saveDietaryPreferences(preferences);
    setStep("goals");
  };
  
  const handleGoalsComplete = async (goals: string[], customGoalText?: string) => { 
    setSelectedGoals(goals); 
    setCustomGoal(customGoalText || "");
    localStorage.setItem("metabolic_onboarded", "true");
    await saveHealthGoals(goals, customGoalText);
    setStep("home"); 
  };

  const handleGenerateList = async () => {
    // Allow generation with either standard goals OR custom goal
    if (selectedGoals.length === 0 && !customGoal.trim()) {
      toast({ title: "Select a goal first", description: "Please choose at least one health goal or enter a custom goal.", variant: "destructive" });
      return;
    }
    
    const primaryGoal = selectedGoals[0] || "custom";
    
    // Build dietary restrictions array for backend
    const dietaryRestrictions = [...localDietaryPreferences.lifestyles, ...localDietaryPreferences.allergens];
    
    try {
      const items = await generateList(selectedGoals, user?.id, customGoal.trim() || undefined, dietaryRestrictions);
      setGroceryList(items);
      localStorage.setItem("metabolic_list", JSON.stringify(items));
      localStorage.setItem("metabolic_current_goal", primaryGoal);
    } catch (err) {
      console.error("Error generating list:", err);
      const errorMessage = err instanceof Error ? err.message : "Could not generate grocery list. Please try again.";
      toast({ 
        title: "Generation Failed", 
        description: errorMessage, 
        variant: "destructive" 
      });
    }
  };

  const handleOpenSwapModal = async (itemId: string) => {
    const currentGoal = localStorage.getItem("metabolic_current_goal") || selectedGoals[0];
    
    // Swap is not supported for custom goals - they don't map to predefined health goals
    if (!currentGoal || currentGoal === "custom") {
      toast({ 
        title: "Swap unavailable", 
        description: "Item swapping is only available for standard health goals.", 
        variant: "default" 
      });
      return;
    }
    
    setSwapItemId(itemId);
    const currentItem = groceryList.find(item => item.id === itemId);
    // Exclude ALL items currently in the list (not just the one being swapped)
    const excludeNames = groceryList.map(item => item.name);
    // Pass dietary restrictions to swap function
    const dietaryRestrictions = [...localDietaryPreferences.lifestyles, ...localDietaryPreferences.allergens];
    const alternatives = await getAlternatives(currentGoal, excludeNames, currentItem?.name, dietaryRestrictions);
    setSwapAlternatives(alternatives);
  };

  const handleCompleteItem = (itemId: string) => {
    setGroceryList((prev) => {
      const updated = prev.map((item) => item.id === itemId ? { ...item, completed: true } : item);
      const sorted = [...updated].sort((a, b) => Number(a.completed) - Number(b.completed));
      if (sorted.every((item) => item.completed) && sorted.length > 0) {
        setTimeout(() => setShowSuccess(true), 300);
      }
      return sorted;
    });
  };

  const handleDeleteItem = (itemId: string) => {
    setGroceryList((prev) => prev.filter((item) => item.id !== itemId));
  };

  const handleSwap = (newItemId: string) => {
    const newItem = swapAlternatives.find((a) => a.id === newItemId);
    if (newItem && swapItemId) setGroceryList((prev) => prev.map((item) => (item.id === swapItemId ? { ...newItem, id: swapItemId } : item)));
    setSwapItemId(null);
  };


  const handleSuccessDone = () => { setShowSuccess(false); setGroceryList([]); localStorage.removeItem("metabolic_list"); };
  
  const handleSignOut = async () => { 
    // Clear all localStorage first
    localStorage.clear(); 
    // Then sign out from Supabase
    await signOut();
    // Reset local state
    setSelectedGoals([]); 
    setGroceryList([]); 
    setViewingHistoryItem(null);
    setCameFromHistory(false);
    setStep("trust"); 
    setActiveTab("home"); 
  };
  
  // Note: Delete account is now handled directly in SettingsView for better UX

  // Track where user came from when viewing history
  const [cameFromHistory, setCameFromHistory] = useState(false);

  // Handle viewing a past list from history
  const handleViewHistoryList = (historyItem: HistoryItem) => {
    setViewingHistoryItem(historyItem);
    setCameFromHistory(true);
    setActiveTab("home");
  };

  // Exit viewing mode and return appropriately based on where user came from
  const handleExitHistoryView = () => {
    setViewingHistoryItem(null);
    if (cameFromHistory) {
      setActiveTab("history");
      setCameFromHistory(false);
    }
  };

  const handleTabChange = (tab: Tab) => {
    // Clear history view when switching tabs
    if (tab !== "home") {
      setViewingHistoryItem(null);
    }
    setActiveTab(tab);
  };
  const currentSwapItem = groceryList.find((item) => item.id === swapItemId);
  const incompleteCount = groceryList.filter((item) => !item.completed).length;

  // Show loading while checking auth
  if (loading) {
    return (
      <MobileContainer>
        <div className="flex items-center justify-center min-h-screen bg-[#D8F3DC]">
          <Loader2 className="w-8 h-8 animate-spin text-[#52B788]" />
        </div>
      </MobileContainer>
    );
  }

  const renderHomeContent = () => {
    if (isGenerating) return <SkeletonLoader />;
    
    // Viewing a past list from history
    if (viewingHistoryItem) {
      const historyIncompleteCount = viewingHistoryItem.items.filter((item) => !item.completed).length;
      return (
        <div className="px-6 py-8 pb-32">
          <div className="mb-4 p-3 rounded-xl bg-metabolic-100 dark:bg-metabolic-900/30 border border-metabolic-300 dark:border-metabolic-700 text-metabolic-800 dark:text-metabolic-200 text-sm flex items-center gap-2">
            <button 
              onClick={handleExitHistoryView}
              className="flex items-center gap-1.5 font-medium hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Current
            </button>
            <span className="text-metabolic-500">•</span>
            <span>Viewing: {viewingHistoryItem.goalName} — {viewingHistoryItem.date}</span>
          </div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Past List</h1>
              <p className="text-sm text-muted-foreground">
                {historyIncompleteCount} of {viewingHistoryItem.items.length} items were remaining
              </p>
            </div>
          </div>
          <div className="space-y-4">
            {viewingHistoryItem.items.map((item) => (
              <GroceryItem 
                key={item.id} 
                item={item} 
                onSwap={() => {}} 
                onComplete={() => {}} 
                onDelete={() => {}} 
                isGuestView={true} // Disable actions for historical view
              />
            ))}
          </div>
        </div>
      );
    }
    
    if (groceryList.length === 0) {
    // Get saved custom goal for display
    const savedCustomGoal = localStorage.getItem("metabolic_custom_goal");
    const allRestrictions = [...localDietaryPreferences.lifestyles, ...localDietaryPreferences.allergens];
    const MAX_VISIBLE_RESTRICTIONS = 4;
    const visibleRestrictions = allRestrictions.slice(0, MAX_VISIBLE_RESTRICTIONS);
    const hiddenCount = allRestrictions.length - MAX_VISIBLE_RESTRICTIONS;
    
    return (
      <div className="flex flex-col h-full">
        <div className="px-6 py-8 space-y-4">
          {/* Row 1: Active Goals */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-medium text-foreground">Active Goals</h2>
              <button onClick={() => setStep("goals")} className="text-sm font-medium flex items-center gap-1 px-3 py-1.5 rounded-lg bg-metabolic-700 text-white hover:bg-metabolic-600 transition-colors">
                <Pencil className="w-3 h-3" /> Edit
              </button>
            </div>
            {selectedGoals.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedGoals.map((goal) => (
                  <span key={goal} className="px-3 py-1.5 rounded-full bg-secondary text-sm text-foreground">
                    {goalLabels[goal]}
                  </span>
                ))}
              </div>
            ) : savedCustomGoal ? (
              <div className="p-3 rounded-xl bg-secondary border border-border">
                <p className="text-sm text-foreground leading-relaxed line-clamp-2">
                  "{savedCustomGoal}"
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No goals set</p>
            )}
          </div>

          {/* Row 2: Dietary Constraints */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-medium text-foreground">Dietary Constraints</h2>
              <Sheet open={isRestrictionsSheetOpen} onOpenChange={setIsRestrictionsSheetOpen}>
                <SheetTrigger asChild>
                  <button className="text-sm font-medium flex items-center gap-1 px-3 py-1.5 rounded-lg bg-metabolic-700 text-white hover:bg-metabolic-600 transition-colors">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-auto max-h-[85vh] rounded-t-3xl">
                  <SheetHeader className="mb-4">
                    <SheetTitle>Edit Dietary Preferences</SheetTitle>
                  </SheetHeader>
                  <DietaryRestrictionsEditor
                    initialPreferences={localDietaryPreferences}
                    onSave={async (prefs) => {
                      setLocalDietaryPreferences(prefs);
                      await saveDietaryPreferences(prefs);
                    }}
                    onClose={() => setIsRestrictionsSheetOpen(false)}
                  />
                </SheetContent>
              </Sheet>
            </div>
            {allRestrictions.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {visibleRestrictions.map((restriction) => (
                  <Badge 
                    key={restriction} 
                    variant="outline" 
                    className="px-2.5 py-1 text-xs font-medium border-amber-400/50 bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-500/30"
                  >
                    {restriction.charAt(0).toUpperCase() + restriction.slice(1).replace(/-/g, ' ')}
                  </Badge>
                ))}
                {hiddenCount > 0 && (
                  <Badge 
                    variant="outline" 
                    className="px-2.5 py-1 text-xs font-medium border-muted-foreground/30 bg-muted text-muted-foreground"
                  >
                    +{hiddenCount} more
                  </Badge>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No restrictions set</p>
            )}
          </div>
        </div>
        
        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-32">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1, y: [0, -5, 0] }} transition={{ y: { duration: 2, repeat: Infinity, ease: "easeInOut" } }} className="w-20 h-20 rounded-3xl bg-secondary flex items-center justify-center mb-6">
            <ShoppingCart className="w-10 h-10 text-foreground" strokeWidth={1.5} />
          </motion.div>
          <h2 className="text-xl font-semibold text-foreground mb-2 text-center">Ready to shop smarter?</h2>
          <p className="text-muted-foreground text-center mb-8 max-w-xs">Generate a grocery list based on your health goals.</p>
          <Button onClick={handleGenerateList} className="h-14 px-8 text-base font-medium gradient-primary hover:opacity-90 transition-opacity relative overflow-hidden">
            <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent" animate={{ x: ["-100%", "100%"] }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }} />
            <span className="relative z-10">Generate Grocery List</span>
          </Button>
        </div>
      </div>
    );
    }
    
    const handleRecipeSearch = () => {
      const itemNames = groceryList.map(item => item.name).join(', ');
      const searchQuery = encodeURIComponent(`healthy recipes using ${itemNames}`);
      window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank');
    };

    const handleCopyList = async () => {
      const itemNames = groceryList.map(item => item.name).join(', ');
      const listText = `🛒 (metabolicapp.com) Grocery List: ${itemNames}`;
      try {
        await navigator.clipboard.writeText(listText);
        toast({ title: "List copied to clipboard!", description: "Paste it anywhere to share." });
      } catch (err) {
        console.error("Failed to copy:", err);
        toast({ title: "Copy failed", description: "Could not copy list to clipboard.", variant: "destructive" });
      }
    };

    const handleClearList = () => {
      setGroceryList([]);
      localStorage.removeItem("metabolic_list");
    };

    return (
      <div className="px-6 py-8 pb-32">
        {isGuestView && <div className="mb-4 p-3 rounded-xl bg-metabolic-100 border border-metabolic-300 text-metabolic-800 text-sm text-center">You are viewing a shared list</div>}
        
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-semibold text-foreground">Your List</h1>
          <p className="text-sm text-muted-foreground">{incompleteCount} of {groceryList.length} items remaining</p>
        </div>

        {/* Smart Action Bar */}
        <div className="flex items-center justify-between w-full mb-6 p-3 rounded-xl bg-secondary/50 border border-border">
          {/* Group A: Productivity (Left Side) */}
          <div className="flex items-center gap-2">
            <button 
              onClick={handleRecipeSearch}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card hover:bg-accent border border-border transition-colors"
              title="Find recipes"
            >
              <ChefHat className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground hidden sm:inline">Recipes</span>
            </button>
            <button 
              onClick={handleCopyList}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card hover:bg-accent border border-border transition-colors"
              title="Copy list to clipboard"
            >
              <Copy className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground hidden sm:inline">Copy</span>
            </button>
          </div>

          {/* Group B: Reset (Right Side) */}
          <button 
            onClick={handleClearList}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-destructive/10 text-destructive transition-colors"
            title="Clear list"
          >
            <Trash2 className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Clear</span>
          </button>
        </div>

        {/* Grocery Items */}
        <div className="space-y-4">
          {groceryList.map((item) => (
            <GroceryItem 
              key={item.id} 
              item={item} 
              onSwap={handleOpenSwapModal} 
              onComplete={handleCompleteItem} 
              onDelete={handleDeleteItem} 
              isGuestView={isGuestView} 
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <MobileContainer>
      <AnimatePresence mode="wait">
        {step === "trust" && <TrustPledgeModal key="trust" onContinue={handleTrustContinue} />}
        {step === "onboarding" && <OnboardingCarousel key="onboarding" onComplete={handleOnboardingComplete} onSkip={handleOnboardingComplete} onBack={handleOnboardingBack} />}
        {step === "auth" && <AuthScreen key="auth" onContinueAsGuest={handleGuestContinue} onAuthSuccess={handleAuthSuccess} onBack={handleAuthBack} />}
        {step === "dietary" && <DietaryPreferences key="dietary" onComplete={handleDietaryComplete} />}
        {step === "goals" && <GoalSelection key="goals" onComplete={handleGoalsComplete} />}
        {step === "home" && (
          <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="min-h-full">
            <div className="sticky top-0 bg-background/80 backdrop-blur-lg border-b border-border z-30">
              <div className="px-6 py-4 flex items-center gap-3"><div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center"><ShoppingCart className="w-5 h-5 text-white" strokeWidth={2} /></div><h1 className="text-xl font-semibold text-foreground">Metabolic</h1></div>
            </div>
            {activeTab === "history" && <HistoryView isGuest={isGuest} userId={user?.id} onViewList={handleViewHistoryList} />}
            {activeTab === "home" && renderHomeContent()}
            {activeTab === "settings" && <SettingsView onSignOut={handleSignOut} isAuthenticated={!!user} isGuest={isGuest} userEmail={user?.email} userPhone={user?.phone} />}
            {!isGuestView && <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />}
          </motion.div>
        )}
      </AnimatePresence>
      <SwapModal isOpen={!!swapItemId} onClose={() => setSwapItemId(null)} currentItem={currentSwapItem || null} alternatives={swapAlternatives} onSwap={handleSwap} isLoading={isLoadingSwap} />
      <AnimatePresence>{showSuccess && <SuccessOverlay goalName={selectedGoals.map((g) => goalLabels[g]).join(" & ")} isCustomGoal={selectedGoals.length === 0 && !!customGoal} onDone={handleSuccessDone} />}</AnimatePresence>
    </MobileContainer>
  );
};

export default Index;
